/**
 * DocumentPickerDialog — pick one or more DMS documents by folder tree + search.
 *
 * Props:
 *   - open: bool
 *   - projectId: string
 *   - multi: bool (default true)
 *   - onClose(): void
 *   - onPick(docs: [Document]): void   // caller decides what to do with the picks
 *   - title?: string
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Folder, FolderOpen, Search, FileText, Check, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const DISCIPLINES = ['CIVIL','STRUCTURAL','MECHANICAL','ELECTRICAL','PROCESS','PIPING','INSTRUMENTATION','ARCHITECTURAL','HSE','GENERAL'];

export default function DocumentPickerDialog({ open, projectId, multi = true, onClose, onPick, title = 'Attach documents' }) {
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState('/root');
  const [q, setQ] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({}); // { docId: doc }

  useEffect(() => {
    if (!open) return;
    setSelected({});
    (async () => {
      try {
        const r = await api.get(`/dms/projects/${projectId}/folders`);  // dms_wf_router
        const list = Array.isArray(r.data) ? r.data : (r.data.folders || []);
        setFolders(list);
      } catch { setFolders([]); }
    })();
  }, [open, projectId]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (discipline) params.discipline = discipline;
      if (folder && folder !== '/root') params.folder = folder;
      // Repointed to the EDM register in S8/W11 — `dms_routes` is gone. The rows are
      // normalised to the shape this dialog already renders rather than renaming the
      // fields at every call site.
      const r = await api.get(`/edm/projects/${projectId}/documents`, { params });
      setDocs((r.data?.documents || []).map((d) => ({
        id: d.document_id,
        doc_number: d.document_no,
        title: d.title,
        discipline: d.discipline,
        folder: d.area,
        status: d.doc_type,
        revision: d.latestRevision?.revision_code,
      })));
    } finally { setBusy(false); }
  }, [projectId, q, folder, discipline]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const treeItems = useMemo(() => {
    const set = new Set(['/root']);
    for (const f of folders) if (f?.path) set.add(f.path);
    return Array.from(set).sort();
  }, [folders]);

  const toggle = (d) => {
    setSelected((s) => {
      const cp = { ...s };
      if (cp[d.id]) delete cp[d.id];
      else if (multi) cp[d.id] = d;
      else return { [d.id]: d };
      return cp;
    });
  };

  const chosen = Object.values(selected);

  const confirm = () => {
    if (chosen.length === 0) return;
    onPick?.(chosen);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl bg-white dark:bg-slate-900">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>

        <div className="grid grid-cols-12 gap-3">
          {/* Folder rail */}
          <div className="col-span-4 border border-slate-200 dark:border-slate-700/50 rounded-md overflow-hidden">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 p-2 border-b border-slate-200 dark:border-slate-700/50">Folders</div>
            <div className="max-h-[380px] overflow-auto text-xs">
              {treeItems.map((p) => (
                <button key={p} onClick={() => setFolder(p)}
                  className={`w-full text-left px-2 py-1.5 flex items-center gap-1.5 hover:bg-slate-100 dark:hover:bg-slate-800/60
                             ${folder === p ? 'bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300' : 'text-slate-700 dark:text-slate-300'}`}
                  data-testid={`docpick-folder-${p}`}
                >
                  {folder === p ? <FolderOpen className="w-3.5 h-3.5" /> : <Folder className="w-3.5 h-3.5" />}
                  <span className="truncate font-mono">{p}</span>
                </button>
              ))}
              {treeItems.length === 1 && <div className="p-3 text-slate-500 text-[11px]">Only /root defined for this project. Create folders in DMS to organise.</div>}
            </div>
          </div>

          {/* Search + list */}
          <div className="col-span-8">
            <div className="flex items-center gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, doc #, tags…" className="pl-7 h-8 text-xs" data-testid="docpick-search" />
              </div>
              <Select value={discipline || 'all'} onValueChange={(v) => setDiscipline(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Discipline" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Disciplines</SelectItem>
                  {DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="border border-slate-200 dark:border-slate-700/50 rounded-md overflow-hidden">
              <div className="px-2 py-1 text-[10px] uppercase text-slate-500 border-b border-slate-200 dark:border-slate-700/50 flex justify-between">
                <span>{docs.length} document(s)</span>
                {chosen.length > 0 && <span className="text-fuchsia-500 font-semibold">{chosen.length} selected</span>}
              </div>
              <div className="max-h-[380px] overflow-auto">
                {busy ? (
                  <div className="p-4 text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
                ) : docs.length === 0 ? (
                  <div className="p-6 text-xs text-slate-500 text-center">No documents found in this scope.</div>
                ) : docs.map((d) => {
                  const isSel = !!selected[d.id];
                  return (
                    <button key={d.id} onClick={() => toggle(d)}
                      className={`w-full text-left text-xs px-2 py-1.5 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700/30
                                 ${isSel ? 'bg-fuchsia-50 dark:bg-fuchsia-500/10' : 'hover:bg-slate-100 dark:hover:bg-slate-800/40'}`}
                      data-testid={`docpick-row-${d.doc_number}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
                                       ${isSel ? 'bg-fuchsia-500 border-fuchsia-500' : 'border-slate-300 dark:border-slate-600'}`}>
                        {isSel && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <FileText className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
                      <span className="font-mono text-slate-500 w-28 truncate">{d.doc_number}</span>
                      <span className="flex-1 truncate text-slate-800 dark:text-slate-100">{d.title}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">{d.discipline}</span>
                      <span className="text-[10px] font-mono text-slate-400">Rev {d.revision}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={chosen.length === 0} className="bg-[#1E4E8C] text-white" data-testid="docpick-confirm">
            Attach {chosen.length > 0 && `(${chosen.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
